import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Wand2, Maximize2, Minimize2, RefreshCw, GraduationCap, Languages, BookMarked, X, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

/**
 * Floating selection toolbar (Sidekick / Jenni-style).
 *
 * Watches text selection inside any element with `data-selection-scope` and
 * shows a small floating toolbar above the selection. Each action sends the
 * selected text to the backend transform endpoint and (on success) calls
 * `onReplace(originalText, newText)` so the parent can patch the source markdown.
 */
export default function SelectionToolbar({ userId, projectId, language = 'en', onReplace }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [selectedText, setSelectedText] = useState('');
  const [busyAction, setBusyAction] = useState(null);
  const toolbarRef = useRef(null);

  // Track selection within data-selection-scope containers
  useEffect(() => {
    const onSelChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) {
        setVisible(false);
        return;
      }
      const range = sel.getRangeAt(0);
      const ancestor = range.commonAncestorContainer.parentElement;
      const scope = ancestor?.closest('[data-selection-scope]');
      if (!scope) {
        setVisible(false);
        return;
      }
      const txt = sel.toString().trim();
      if (txt.length < 10) {
        setVisible(false);
        return;
      }
      const rect = range.getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) return;
      setSelectedText(txt);
      setPos({
        top: window.scrollY + rect.top - 50,
        left: window.scrollX + rect.left + rect.width / 2,
      });
      setVisible(true);
    };
    document.addEventListener('selectionchange', onSelChange);
    return () => document.removeEventListener('selectionchange', onSelChange);
  }, []);

  const runAction = useCallback(async (action) => {
    if (!selectedText) return;
    setBusyAction(action);
    try {
      const res = await thesisApi.transformText(userId, projectId, action, selectedText, language);
      const newText = (res?.transformed || '').trim();
      if (!newText) {
        toast.warning('AI returned an empty result. Try a longer selection.');
        return;
      }
      onReplace?.(selectedText, newText, action);
      toast.success(`${action.replace('_', ' ')} applied`);
      window.getSelection()?.removeAllRanges();
      setVisible(false);
    } catch (e) {
      toast.error('Action failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusyAction(null);
    }
  }, [selectedText, userId, projectId, language, onReplace]);

  if (!visible) return null;

  return (
    <div
      ref={toolbarRef}
      className="fixed z-50 flex items-center gap-0.5 px-1.5 py-1 rounded-full bg-[#0b1a35] dark:bg-[#0a0e1a] shadow-xl border border-white/10"
      style={{
        top: pos.top,
        left: pos.left,
        transform: 'translateX(-50%)',
        backdropFilter: 'blur(8px)',
      }}
      data-testid="selection-toolbar"
    >
      <ToolbarButton title="Expand" icon={Maximize2} action="expand" busy={busyAction} onClick={runAction} testid="sel-expand" />
      <ToolbarButton title="Shorten" icon={Minimize2} action="shorten" busy={busyAction} onClick={runAction} testid="sel-shorten" />
      <ToolbarButton title="Rewrite" icon={RefreshCw} action="rewrite" busy={busyAction} onClick={runAction} testid="sel-rewrite" />
      <ToolbarButton title="Academic tone" icon={GraduationCap} action="academic_tone" busy={busyAction} onClick={runAction} testid="sel-academic" />
      <ToolbarButton title="Add citation" icon={BookMarked} action="add_citation" busy={busyAction} onClick={runAction} testid="sel-cite" />
      <ToolbarButton title="Translate" icon={Languages} action="translate" busy={busyAction} onClick={runAction} testid="sel-translate" />
      <div className="w-px h-5 bg-white/15 mx-0.5" />
      <button
        type="button"
        onClick={() => { setVisible(false); window.getSelection()?.removeAllRanges(); }}
        className="h-7 w-7 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
        data-testid="sel-close"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function ToolbarButton({ title, icon: Icon, action, busy, onClick, testid }) {
  const isBusy = busy === action;
  return (
    <button
      type="button"
      title={title}
      onClick={() => onClick(action)}
      disabled={!!busy}
      className="h-7 w-7 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-[#1d4ed8] transition-colors disabled:opacity-50"
      data-testid={testid}
    >
      {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
    </button>
  );
}
